import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ManagedUser } from '../../types/auth.types';
import { useUpdateUser } from '../../hooks/useUserQueries';
import { LedgerDialog, LedgerField, LedgerInput } from '../ui';

const schema = yup.object({
  firstName: yup
    .string()
    .default('')
    .required('First name is required')
    .test('non-blank', 'First name is required', (value) => Boolean(value && value.trim()))
    .max(150, 'First name must be 150 characters or fewer'),
  lastName: yup
    .string()
    .default('')
    .required('Last name is required')
    .test('non-blank', 'Last name is required', (value) => Boolean(value && value.trim()))
    .max(150, 'Last name must be 150 characters or fewer'),
});

type ProfileEditFormValues = yup.InferType<typeof schema>;

interface ProfileEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: ManagedUser;
}

export const ProfileEditDialog: React.FC<ProfileEditDialogProps> = ({
  open,
  onClose,
  onSuccess,
  user,
}) => {
  const [error, setError] = useState<string | null>(null);
  const firstNameInputRef = useRef<HTMLInputElement>(null);

  const updateUser = useUpdateUser();
  const saving = updateUser.isPending;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ProfileEditFormValues>({
    resolver: yupResolver(schema),
    defaultValues: { firstName: user.firstName, lastName: user.lastName },
    mode: 'onTouched',
  });

  // Reseed whenever the dialog opens or its subject changes so a stale edit
  // subject or error strip never leaks into the next open.
  useEffect(() => {
    setError(null);
    reset({ firstName: user.firstName, lastName: user.lastName });
  }, [user, open, reset]);

  const handleClose = () => {
    reset({ firstName: user.firstName, lastName: user.lastName });
    setError(null);
    onClose();
  };

  const onSubmit = async (values: ProfileEditFormValues) => {
    setError(null);
    try {
      await updateUser.mutateAsync({
        id: user.id,
        data: { firstName: values.firstName, lastName: values.lastName },
      });
      onSuccess();
      handleClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail || 'Failed to update profile');
    }
  };

  const firstNameField = register('firstName');

  return (
    <LedgerDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      title="Edit Profile"
      size="sm"
      preventClose={saving}
      initialFocus={firstNameInputRef}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised disabled:cursor-default disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="profile-edit-form"
            disabled={saving || isSubmitting}
            className="cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover disabled:cursor-default disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Update'}
          </button>
        </>
      }
    >
      <form id="profile-edit-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {error && (
          <p role="alert" className="mb-3 border border-sev-critical/40 px-3 py-2 text-scale-1 text-sev-critical">
            {error}
          </p>
        )}

        <LedgerField label="First Name" htmlFor="profile-first-name" error={errors.firstName?.message}>
          <LedgerInput
            id="profile-first-name"
            required
            invalid={!!errors.firstName}
            {...firstNameField}
            ref={(element) => {
              firstNameField.ref(element);
              firstNameInputRef.current = element;
            }}
          />
        </LedgerField>

        <LedgerField label="Last Name" htmlFor="profile-last-name" error={errors.lastName?.message}>
          <LedgerInput
            id="profile-last-name"
            required
            invalid={!!errors.lastName}
            {...register('lastName')}
          />
        </LedgerField>
      </form>
    </LedgerDialog>
  );
};
